import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import {
  BookingModuleSettingsError,
  BookingModuleSaveError,
  OwnerBookingModule,
  OwnerBookingModuleChange,
  OwnerBookingModuleSettings,
  OwnerBookingModuleUpdate,
} from '../types/bookingModuleTypes';

type BookingModuleState = {
  modules: OwnerBookingModule[] | null;
  draftsById: Record<string, OwnerBookingModuleUpdate>;
  isLoading: boolean;
  loadError: BookingModuleSettingsError | null;
  isSaving: boolean;
  savingById: Record<string, boolean>;
  saveErrorsById: Record<string, BookingModuleSaveError>;
  invalidThemeColorById: Record<string, boolean>;
  slugErrorsById: Record<string, string>;
};

type BookingModuleRootState = { bookingModules: BookingModuleState };

const settingsFields: (keyof OwnerBookingModuleSettings)[] = [
  'booking_page_enabled',
  'integration_enabled',
  'background_fill',
  'theme_color',
  'slug',
];

const initialState: BookingModuleState = {
  modules: null,
  draftsById: {},
  isLoading: false,
  loadError: null,
  isSaving: false,
  savingById: {},
  saveErrorsById: {},
  invalidThemeColorById: {},
  slugErrorsById: {},
};

const BookingModuleSlice = createSlice({
  name: 'bookingModules',
  initialState,
  reducers: {
    getOwnerBookingModules(state) {
      if (state.isSaving) return;
      state.modules = null;
      state.isLoading = true;
      state.loadError = null;
      state.draftsById = {};
      state.saveErrorsById = {};
      state.invalidThemeColorById = {};
      state.slugErrorsById = {};
    },
    setOwnerBookingModules(state, action: PayloadAction<OwnerBookingModule[]>) {
      if (state.isSaving) return;
      state.modules = action.payload;
      state.draftsById = {};
      state.isLoading = false;
      state.loadError = null;
      state.invalidThemeColorById = {};
      state.slugErrorsById = {};
    },
    setOwnerBookingModulesError(
      state,
      action: PayloadAction<BookingModuleSettingsError>,
    ) {
      state.isLoading = false;
      state.loadError = action.payload;
    },
    setOwnerBookingModuleThemeColorValidity(
      state,
      action: PayloadAction<{ id: string; isValid: boolean }>,
    ) {
      const { id, isValid } = action.payload;
      if (isValid) {
        delete state.invalidThemeColorById[id];
      } else {
        state.invalidThemeColorById[id] = true;
      }
    },
    updateOwnerBookingModuleDraft(
      state,
      action: PayloadAction<OwnerBookingModuleChange>,
    ) {
      if (state.isSaving || state.isLoading) return;
      const { id, data } = action.payload;
      const module = state.modules?.find((item) => item.id === id);
      if (!module) return;

      const draft = { ...state.draftsById[id], ...data };
      settingsFields.forEach((field) => {
        if (draft[field] === undefined || draft[field] === module[field]) {
          delete draft[field];
        }
      });

      if (Object.keys(draft).length) {
        state.draftsById[id] = draft;
      } else {
        delete state.draftsById[id];
      }
      if (data.slug !== undefined) delete state.slugErrorsById[id];
      delete state.saveErrorsById[id];
    },
    saveOwnerBookingModules(state) {
      if (state.isSaving || state.isLoading) return;
      if (Object.keys(state.invalidThemeColorById).length) return;
      const ids = Object.keys(state.draftsById);
      if (!ids.length) return;
      state.isSaving = true;
      ids.forEach((id) => {
        state.savingById[id] = true;
        delete state.saveErrorsById[id];
      });
    },
    finishOwnerBookingModulesSave(state) {
      state.isSaving = false;
      state.savingById = {};
    },
    replaceOwnerBookingModule(
      state,
      action: PayloadAction<OwnerBookingModule>,
    ) {
      if (state.modules) {
        state.modules = state.modules.map((module) =>
          module.id === action.payload.id ? action.payload : module,
        );
      }
      delete state.draftsById[action.payload.id];
      delete state.savingById[action.payload.id];
      delete state.saveErrorsById[action.payload.id];
      delete state.invalidThemeColorById[action.payload.id];
      delete state.slugErrorsById[action.payload.id];
    },
    setOwnerBookingModuleSlugError(
      state,
      action: PayloadAction<{ id: string; error: string }>,
    ) {
      state.slugErrorsById[action.payload.id] = action.payload.error;
    },
    setOwnerBookingModuleSaveError(
      state,
      action: PayloadAction<{ id: string; error: BookingModuleSaveError }>,
    ) {
      const { id, error } = action.payload;
      delete state.savingById[id];
      state.saveErrorsById[id] = error;
    },
  },
});

export const {
  getOwnerBookingModules,
  setOwnerBookingModules,
  setOwnerBookingModulesError,
  setOwnerBookingModuleThemeColorValidity,
  updateOwnerBookingModuleDraft,
  saveOwnerBookingModules,
  finishOwnerBookingModulesSave,
  replaceOwnerBookingModule,
  setOwnerBookingModuleSaveError,
  setOwnerBookingModuleSlugError,
} = BookingModuleSlice.actions;

export default BookingModuleSlice.reducer;

export const bookingModuleName = BookingModuleSlice.name;

const getModules = (state: BookingModuleRootState) =>
  state.bookingModules.modules;
const getDrafts = (state: BookingModuleRootState) =>
  state.bookingModules.draftsById;

export const BookingModuleSelectors = {
  getModules,
  getIsLoading: (state: BookingModuleRootState) =>
    state.bookingModules.isLoading,
  getLoadError: (state: BookingModuleRootState) =>
    state.bookingModules.loadError,
  getIsSaving: (state: BookingModuleRootState) => state.bookingModules.isSaving,
  getHasChanges: (state: BookingModuleRootState) =>
    Object.keys(state.bookingModules.draftsById).length > 0,
  getHasInvalidThemeColor: (state: BookingModuleRootState) =>
    Object.keys(state.bookingModules.invalidThemeColorById).length > 0,
  getChanges: createSelector(
    [getModules, getDrafts],
    (modules, drafts): OwnerBookingModuleChange[] =>
      (modules || [])
        .filter((module) => Boolean(drafts[module.id]))
        .map((module) => ({ id: module.id, data: drafts[module.id] })),
  ),
  getDraftById: (id: string) => (state: BookingModuleRootState) =>
    state.bookingModules.draftsById[id] || null,
  getSavingById: (id: string) => (state: BookingModuleRootState) =>
    Boolean(state.bookingModules.savingById[id]),
  getSaveError: (id: string) => (state: BookingModuleRootState) =>
    state.bookingModules.saveErrorsById[id] || null,
  getThemeColorInvalid: (id: string) => (state: BookingModuleRootState) =>
    Boolean(state.bookingModules.invalidThemeColorById[id]),
  getSlugError: (id: string) => (state: BookingModuleRootState) =>
    state.bookingModules.slugErrorsById[id] || null,
};
